/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import VERTC, { MediaType } from '@volcengine/rtc';
import { Modal } from '@arco-design/web-react';
import RtcClient from '@/lib/RtcClient';
import {
  clearCurrentMsg,
  clearHistoryMsg,
  localJoinRoom,
  localLeaveRoom,
  updateAIGCState,
  updateLocalUser,
} from '@/store/slices/room';

import useRtcListeners from '@/lib/listenerHooks';
import { RootState } from '@/store';

import {
  updateMediaInputs,
  updateSelectedDevice,
  setDevicePermissions,
} from '@/store/slices/device';
import logger from '@/utils/logger';
import { refreshSceneRuntime } from '@/lib/sceneRuntime';

export const ABORT_VISIBILITY_CHANGE = 'abortVisibilityChange';
export interface FormProps {
  username: string;
  roomId: string;
  publishAudio: boolean;
}

export const useScene = () => {
  const { scene, sceneConfigMap } = useSelector((state: RootState) => state.room);
  return sceneConfigMap[scene] || {};
}

export const useRTC = () => {
  const { scene, rtcConfigMap } = useSelector((state: RootState) => state.room);
  return rtcConfigMap[scene] || {};
}

export const useDeviceState = () => {
  const dispatch = useDispatch();
  const room = useSelector((state: RootState) => state.room);
  const localUser = room.localUser;
  const isAudioPublished = localUser.publishAudio;
  const isVideoPublished = localUser.publishVideo;
  const isScreenPublished = localUser.publishScreen;
  const queryDevices = async (type: MediaType) => {
    const mediaDevices = await RtcClient.getDevices({
      audio: type === MediaType.AUDIO,
      video: type === MediaType.VIDEO,
    });
    if (type === MediaType.AUDIO) {
      dispatch(
        updateMediaInputs({
          audioInputs: mediaDevices.audioInputs,
        })
      );
      dispatch(
        updateSelectedDevice({
          selectedMicrophone: mediaDevices.audioInputs[0]?.deviceId,
        })
      );
    } else {
      dispatch(
        updateMediaInputs({
          videoInputs: mediaDevices.videoInputs,
        })
      );
      dispatch(
        updateSelectedDevice({
          selectedCamera: mediaDevices.videoInputs[0]?.deviceId,
        })
      );
    }
    return mediaDevices;
  };

  const switchMic = async (controlPublish = true) => {
    const mediaDevices = await queryDevices(MediaType.AUDIO);
    if (!mediaDevices.audioInputs.length && !isAudioPublished) {
      return false;
    }
    if (!isAudioPublished) {
      await RtcClient.startAudioCapture();
      if (controlPublish) {
        await RtcClient.publishStream(MediaType.AUDIO);
      }
    } else {
      if (controlPublish) {
        await RtcClient.unpublishStream(MediaType.AUDIO);
      }
      await RtcClient.stopAudioCapture();
    }
    dispatch(
      updateLocalUser({
        publishAudio: !isAudioPublished,
      })
    );
    return true;
  };

  const switchCamera = async (controlPublish = true) => {
    const mediaDevices = await queryDevices(MediaType.VIDEO);
    if (!mediaDevices.videoInputs.length && !isVideoPublished) {
      return false;
    }
    if (!isVideoPublished) {
      await RtcClient.startVideoCapture();
      if (controlPublish) {
        await RtcClient.publishStream(MediaType.VIDEO);
      }
    } else {
      if (controlPublish) {
        await RtcClient.unpublishStream(MediaType.VIDEO);
      }
      await RtcClient.stopVideoCapture();
    }
    dispatch(
      updateLocalUser({
        publishVideo: !isVideoPublished,
      })
    );
    return true;
  };

  const switchScreenCapture = async (controlPublish = true) => {
    try {
      !isScreenPublished
        ? sessionStorage.setItem(ABORT_VISIBILITY_CHANGE, 'true')
        : sessionStorage.removeItem(ABORT_VISIBILITY_CHANGE);
      if (controlPublish) {
        await (!isScreenPublished
          ? RtcClient.publishScreenStream(MediaType.VIDEO)
          : RtcClient.unpublishScreenStream(MediaType.VIDEO));
      }
      await (!isScreenPublished ? RtcClient.startScreenCapture() : RtcClient.stopScreenCapture());
      dispatch(
        updateLocalUser({
          publishScreen: !isScreenPublished,
        })
      );
    } catch {
      console.warn('Not Authorized.');
    }
    sessionStorage.removeItem(ABORT_VISIBILITY_CHANGE);
    return false;
  };

  return {
    isAudioPublished,
    isVideoPublished,
    isScreenPublished,
    switchMic,
    switchCamera,
    switchScreenCapture,
  };
};

export const useGetDevicePermission = () => {
  const [permission, setPermission] = useState<{
    audio: boolean;
  }>();

  const dispatch = useDispatch();

  useEffect(() => {
    (async () => {
      const permission = await RtcClient.checkPermission();
      dispatch(setDevicePermissions(permission));
      setPermission(permission);
    })();
  }, [dispatch]);
  return permission;
};

export const useJoin = (): [
  boolean,
  (options?: { enableMicOnJoin?: boolean }) => Promise<boolean | undefined>
] => {
  const devicePermissions = useSelector((state: RootState) => state.device.devicePermissions);
  const room = useSelector((state: RootState) => state.room);

  const dispatch = useDispatch();

  const { id } = useScene();
  const { switchMic } = useDeviceState();
  const [joining, setJoining] = useState(false);
  const listeners = useRtcListeners();

  const handleAIGCModeStart = async () => {
    if (room.isAIGCEnable) {
      await RtcClient.stopAgent(id);
      dispatch(clearCurrentMsg());
      await RtcClient.startAgent(id);
    } else {
      await RtcClient.startAgent(id);
    }
    dispatch(updateAIGCState({ isAIGCEnable: true }));
  };

  async function disPatchJoin(options?: {
    enableMicOnJoin?: boolean;
  }): Promise<boolean | undefined> {
    const { enableMicOnJoin = true } = options || {};
    if (joining) {
      // Joining already in progress; let callers wait for isJoined.
      return true;
    }

    if (room.isJoined) {
      if (!room.isAIGCEnable) {
        await handleAIGCModeStart();
      }
      if (enableMicOnJoin && !room.localUser.publishAudio && devicePermissions.audio) {
        try {
          await switchMic();
        } catch (e) {
          logger.debug('No permission for mic');
        }
      }
      return true;
    }

    await refreshSceneRuntime(dispatch, room.scene, room.sceneConfigMap);

    // Validate required RTC config before trying to create engine/join.
    // This avoids hard-crashing when the demo isn't configured yet.
    const { app_id: appId, room_id: roomId, user_id: userId, token } =
      RtcClient.basicInfo || ({} as any);
    if (!appId || !roomId || !userId || !token) {
      Modal.error({
        title: '缺少 RTC 配置',
        content:
          '请先在 `Server/scenes/Custom.json` 填写 RTCConfig.AppId / RTCConfig.AppKey（或通过环境变量 RTC_APP_ID / RTC_APP_KEY 注入），然后刷新页面重试。',
      });
      return false;
    }

    const isSupported = await VERTC.isSupported();
    if (!isSupported) {
      Modal.error({
        title: '不支持 RTC',
        content: '您的浏览器可能不支持 RTC 功能，请尝试更换浏览器或升级浏览器后再重试。',
      });
      return false;
    }

    try {
      setJoining(true);

      /** 1. Create RTC Engine */
      await RtcClient.createEngine();

      /** 2.1 Set events callbacks */
      RtcClient.addEventListeners(listeners);

      /** 2.2 RTC starting to join room */
      await RtcClient.joinRoom();
      /** 3. Set users' devices info */
      const mediaDevices = await RtcClient.getDevices({
        audio: true,
        video: false,
      });

      dispatch(
        localJoinRoom({
          roomId: RtcClient.basicInfo.room_id,
          user: {
            username: RtcClient.basicInfo.user_id,
            userId: RtcClient.basicInfo.user_id,
          },
        })
      );
      dispatch(
        updateSelectedDevice({
          selectedMicrophone: mediaDevices.audioInputs[0]?.deviceId,
          selectedCamera: mediaDevices.videoInputs[0]?.deviceId,
        })
      );
      dispatch(updateMediaInputs(mediaDevices));

      if (enableMicOnJoin && devicePermissions.audio) {
        try {
          await switchMic();
        } catch (e) {
          logger.debug('No permission for mic');
        }
      }

      await handleAIGCModeStart();
      return true;
    } catch (e: any) {
      Modal.error({
        title: '入会失败',
        content: e?.message ? String(e.message) : String(e),
      });
      return false;
    } finally {
      setJoining(false);
    }
  }

  return [joining, disPatchJoin];
};

export const useLeave = () => {
  const dispatch = useDispatch();
  const { id } = useScene();
  const idRef = useRef(id);
  idRef.current = id;

  return async function () {
    await Promise.all([
      RtcClient.stopAudioCapture().catch(() => undefined),
      RtcClient.stopScreenCapture().catch(() => undefined),
      RtcClient.stopVideoCapture().catch(() => undefined),
    ]);
    await RtcClient.stopAgent(idRef.current);
    await RtcClient.leaveRoom();
    dispatch(clearHistoryMsg());
    dispatch(clearCurrentMsg());
    dispatch(localLeaveRoom());
    dispatch(updateAIGCState({ isAIGCEnable: false }));
  };
};
